import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Typography,
} from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Alert, { type Color } from "@material-ui/lab/Alert";
import clsx from "clsx";
import "date-fns";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Question } from "../../../models/api/question";
import type { Request } from "../../../models/api/request";
import type { Navigation } from "../../../models/navigation";
import { Header } from "../../../shared/components/header";
import { useInIframe } from "../../../shared/hooks";
import { setClose } from "../../../shared/request/actions/requestActions";
import type { RootState } from "../../../store";
import { asyncGetOrganizationFaq } from "../../actions/bucketActions";
import { participantFAQStyles as useStyles } from "./participant-FAQ-page.styles";

interface SelectorInterface {
  request: Request;
  organization: string;
  questions: Question[];
}

interface FAQProps {
  navigation: Navigation[];
}

export const ParticipantFAQPage = ({ navigation }: FAQProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isInIframe = useInIframe();

  const { request, questions, organization } = useSelector<
    RootState,
    SelectorInterface
  >((state) => {
    return {
      questions: state.bucketReducer.faq,
      request: state.request.request,
      organization: state.profile.organizationId,
    };
  });

  const handleClose = () => {
    dispatch(setClose());
  };

  useEffect(() => {
    dispatch(asyncGetOrganizationFaq(organization));
  }, [dispatch, organization]);

  const questionNumber = (n: number): string | number => (n < 10 ? `0${n}` : n);

  return (
    <>
      <Header navigation={navigation}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          {!isInIframe && (
            <Typography className={classes.title}>FAQ</Typography>
          )}
          <Typography className={classes.subtitle}>
            Hi, how can we help?
          </Typography>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          {!questions.length ? (
            <Paper className={classes.paper}>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Typography align="center" variant="h6">
                  You have no FAQ assigned yet.
                </Typography>
              </Grid>
            </Paper>
          ) : (
            <List>
              {questions.map((row, i) => {
                return (
                  <ListItem
                    key={row.question}
                    ContainerComponent="div"
                    className={classes.questionContainer}
                  >
                    <Grid item md={8} sm={12} xs={12}>
                      <ListItemText className={classes.questionContainer}>
                        <Accordion square className={classes.accordion}>
                          <AccordionSummary
                            expandIcon={
                              <IconButton
                                color="secondary"
                                className={classes.arrowIcon}
                              >
                                <ExpandMoreIcon />
                              </IconButton>
                            }
                          >
                            <Typography
                              className={clsx(
                                classes.question,
                                classes.textWrap
                              )}
                            >
                              <span className={classes.questionNumber}>
                                {questionNumber(i + 1)}
                              </span>
                              {row.question}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography
                              className={clsx(classes.answer, classes.textWrap)}
                            >
                              {row.answer}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      </ListItemText>
                    </Grid>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Grid>
        <Grid item className={classes.contact}>
          <Typography className={classes.contactMessage}>
            Still have a question?
            <a
              href="mailto:helpdesk@meetfruition.com"
              className={classes.buttonContact}
            >
              Contact us
            </a>
          </Typography>
        </Grid>
        {request.isOpen && (
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            open={request.isOpen}
            onClose={handleClose}
          >
            <Alert onClose={handleClose} severity={request.type as Color}>
              {request.message}
            </Alert>
          </Snackbar>
        )}
      </Header>
    </>
  );
};
